import type { ShareInput } from './doctor-share-dialog';
import type {
  BillingServiceModel,
  CodeDisplayName,
  ProviderFeeShare,
  ProviderServiceFee,
  VisitFee,
} from '@/shared/types/billing';
import type { OrgLocation } from '@/auth/types';

/**
 * Shared draft model + payload assembly for the Provider Fee create banner
 * (`provider-fee-add-row.tsx`) and the inline edit row
 * (`provider-fee-edit-row.tsx`).
 *
 * PM parity: the duplicated `Draft` / `buildPayload` / `draftFromFee` in the
 * two PM row components. Consolidated here so create and edit stay in lockstep.
 * All wire field names preserved verbatim.
 */

interface DraftLocationFee {
  fee: string;
  serviceFee?: number;
}

export interface ProviderFeeDraft {
  serviceId?: string;
  procedureCode?: CodeDisplayName;
  serviceQuery: string;
  wTax: string;
  /** Keyed by location.id. */
  fees: Record<string, DraftLocationFee>;
  /** Keyed by location.id. */
  shares: Record<string, ShareInput>;
}

export function blankDraft(): ProviderFeeDraft {
  return {
    serviceId: undefined,
    procedureCode: undefined,
    serviceQuery: '',
    wTax: '',
    fees: {},
    shares: {},
  };
}

/** Seed a draft from an existing provider fee (EDIT mode). */
export function draftFromFee(
  existing: ProviderServiceFee,
  locations: OrgLocation[],
): ProviderFeeDraft {
  const fees: Record<string, DraftLocationFee> = {};
  const shares: Record<string, ShareInput> = {};
  for (const loc of locations) {
    if (!loc.id) continue;
    const provFee = existing.providerVisitFees?.find((v) => v.location?.id === loc.id);
    const svcFee = existing.serviceVisitFees?.find((v) => v.location?.id === loc.id);
    fees[loc.id] = {
      fee: provFee?.fee != null ? String(provFee.fee) : '',
      serviceFee: svcFee?.fee,
    };
    const share = existing.providerFeeShares?.find((s) => s.location?.id === loc.id);
    if (share) {
      shares[loc.id] = {
        isPercent: share.isPercent ?? true,
        cash: share.percent?.cash != null ? String(share.percent.cash) : '',
        insurance: share.percent?.insurance != null ? String(share.percent.insurance) : '',
        corporate: share.percent?.corporate != null ? String(share.percent.corporate) : '',
        fixed: share.fixed != null ? String(share.fixed) : '',
      };
    }
  }
  return {
    serviceId: existing.serviceId,
    procedureCode: existing.procedureCode,
    serviceQuery: existing.procedureCode?.displayName ?? '',
    wTax: existing.wTax != null ? String(existing.wTax) : '',
    fees,
    shares,
  };
}

/** Re-seed a draft after picking a service in the typeahead. */
export function draftFromService(
  prev: ProviderFeeDraft,
  svc: BillingServiceModel,
  locations: OrgLocation[],
): ProviderFeeDraft {
  const next: ProviderFeeDraft = {
    ...prev,
    serviceId: svc.id ?? undefined,
    procedureCode: svc.procedureCode ?? undefined,
    serviceQuery: svc.procedureCode?.displayName ?? '',
    fees: {},
    // Shares are relative to the previous service's per-location fees — clear
    // them when the service changes, else stale shares for the old service
    // ride through to the payload for locations the new service may not fee
    // (PROD-1210 Codex review). buildPayload also guards by fee as defence-in-depth.
    shares: {},
  };
  for (const loc of locations) {
    if (!loc.id) continue;
    const svcFee = svc.visitFees?.find((v) => v.location?.id === loc.id);
    next.fees[loc.id] = {
      fee: svcFee?.fee != null ? String(svcFee.fee) : '',
      serviceFee: svcFee?.fee,
    };
  }
  return next;
}

/**
 * Build the create/update payload. On EDIT (`editing` provided) the existing
 * record is spread first so untouched fields round-trip intact, and existing
 * per-location fee / share ids are preserved.
 */
export function buildPayload(
  draft: ProviderFeeDraft,
  locations: OrgLocation[],
  providerId: string,
  editing: ProviderServiceFee | null,
): ProviderServiceFee {
  const providerVisitFees: VisitFee[] = [];
  for (const loc of locations) {
    if (!loc.id) continue;
    const row = draft.fees[loc.id];
    if (!row?.fee.trim()) continue;
    const n = parseFloat(row.fee);
    if (Number.isNaN(n)) continue;
    const existing = editing?.providerVisitFees?.find((v) => v.location?.id === loc.id);
    providerVisitFees.push({
      id: existing?.id,
      fee: n,
      location: { id: loc.id, name: loc.name },
      isArchived: false,
    });
  }

  // Only emit a share for a location that actually has a fee row above — a
  // doctor-share has no meaning without a per-location fee, and a stale share
  // for a fee-less location would corrupt the create/update payload
  // (PROD-1210 Codex review).
  const feeLocationIds = new Set(
    providerVisitFees.map((v) => v.location?.id).filter(Boolean),
  );
  const providerFeeShares: ProviderFeeShare[] = [];
  for (const loc of locations) {
    if (!loc.id) continue;
    if (!feeLocationIds.has(loc.id)) continue;
    const share = draft.shares[loc.id];
    if (!share) continue;
    const existing = editing?.providerFeeShares?.find((s) => s.location?.id === loc.id);
    const base: ProviderFeeShare = {
      id: existing?.id,
      providerId,
      location: { id: loc.id, name: loc.name },
      isArchived: false,
      isPercent: share.isPercent,
    };
    if (share.isPercent) {
      base.percent = {
        cash: parseFloat(share.cash) || 0,
        insurance: parseFloat(share.insurance) || 0,
        corporate: parseFloat(share.corporate) || 0,
      };
    } else {
      base.fixed = parseFloat(share.fixed) || 0;
    }
    providerFeeShares.push(base);
  }

  const wTaxParsed = draft.wTax.trim() ? parseFloat(draft.wTax) : NaN;

  return {
    ...(editing ?? {}),
    id: editing?.id,
    providerId,
    serviceId: draft.serviceId,
    procedureCode: draft.procedureCode,
    wTax: Number.isNaN(wTaxParsed) ? undefined : wTaxParsed,
    serviceVisitFees: editing?.serviceVisitFees,
    providerVisitFees,
    providerFeeShares,
    isArchived: editing?.isArchived ?? false,
  };
}

// ─── Share preview formatting ────────────────────────────────────────────

export function formatTerse(n: number): string {
  return n
    .toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    .replace(/\.00$/, '');
}

function cleanInt(n: number): number {
  return Number.isNaN(n) ? 0 : n;
}

/** Compact single-line preview shown beneath each fee input. */
export function formatSharePreview(share: ShareInput | undefined): string | null {
  if (!share) return null;
  if (!share.isPercent) {
    const v = parseFloat(share.fixed);
    if (Number.isNaN(v)) return null;
    return `Fixed: ${formatTerse(v)}`;
  }
  const c = parseFloat(share.cash);
  const i = parseFloat(share.insurance);
  const co = parseFloat(share.corporate);
  if (Number.isNaN(c) && Number.isNaN(i) && Number.isNaN(co)) return null;
  return `C: ${cleanInt(c)}%, I: ${cleanInt(i)}%, Co: ${cleanInt(co)}%`;
}
